import { useState, useEffect, useCallback } from 'react';
import logger from '../lib/logger';
import { useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { getWalletBalances, getTransactions, getTransactionStats, getDepositStatus, addCurrency, getCurrencies } from '../lib/api';
import { toast } from 'sonner';
import { useTranslation } from '../i18n';
import { useLocaleFormatters } from '../hooks/useLocaleFormatters';
import {
  Wallet,
  ArrowUpRight,
  ArrowDownLeft,
  Plus,
  RefreshCw,
  CreditCard,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Loader2,
  Coins,
  Globe
} from 'lucide-react';
import { motion } from 'framer-motion';
import SpendingLimitAlert from '../components/SpendingLimitAlert';
import QuickExchangeWidget from '../components/QuickExchangeWidget';
import ReceiptButton from '../components/ReceiptButton';
import DashboardTagWidget from '../components/DashboardTagWidget';

const Dashboard = () => {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useLocaleFormatters();
  const [searchParams] = useSearchParams();
  const [balances, setBalances] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingDeposit, setCheckingDeposit] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [addingCurrency, setAddingCurrency] = useState(false);
  const [showAddCurrencyDialog, setShowAddCurrencyDialog] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [balancesRes, transactionsRes, statsRes, currenciesRes] = await Promise.all([
        getWalletBalances(),
        getTransactions(5),
        getTransactionStats(),
        getCurrencies()
      ]);
      setBalances(balancesRes.data);
      setTransactions(transactionsRes.data);
      setStats(statsRes.data);
      setCurrencies(currenciesRes.data);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const checkDepositStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setCheckingDeposit(false);
      return;
    }

    try {
      const response = await getDepositStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success(`Deposit successful! $${response.data.amount} added to your wallet.`);
        fetchData();
        setCheckingDeposit(false);
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        setCheckingDeposit(false);
      } else {
        setTimeout(() => checkDepositStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      logger.error('Error checking deposit status:', error);
      setCheckingDeposit(false);
    }
  }, [fetchData]);

  useEffect(() => {
    fetchData();
    
    const sessionId = searchParams.get('session_id');
    const depositSuccess = searchParams.get('deposit');
    
    if (sessionId && depositSuccess === 'success') {
      setCheckingDeposit(true);
      checkDepositStatus(sessionId);
    }
  }, [fetchData, searchParams, checkDepositStatus]);

  const getTotalBalance = () => {
    return balances.reduce((sum, b) => sum + b.balance, 0).toFixed(2);
  };

  const getAvailableCurrencies = () => {
    const existingCodes = balances.map(b => b.currency);
    return currencies.filter(c => !existingCodes.includes(c.code));
  };

  const handleAddCurrency = async () => {
    if (!selectedCurrency) {
      toast.error('Please select a currency');
      return;
    }
    
    setAddingCurrency(true);
    try {
      await addCurrency(selectedCurrency);
      toast.success(`${selectedCurrency} wallet added successfully!`);
      setShowAddCurrencyDialog(false);
      setSelectedCurrency('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add currency');
    } finally {
      setAddingCurrency(false);
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="w-4 h-4 text-emerald-500" />;
      case 'send':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'receive':
        return <ArrowDownLeft className="w-4 h-4 text-emerald-500" />;
      case 'exchange':
        return <RefreshCw className="w-4 h-4 text-blue-500" />;
      default:
        return <Wallet className="w-4 h-4" />;
    }
  };

  const formatAmount = (type, amount, currency) => {
    const prefix = type === 'send' ? '-' : '+';
    return `${prefix}${formatCurrency(amount, currency)}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div 
        className="space-y-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        data-testid="dashboard-container"
      >
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold">{t('dashboard.title')}</h1>
            <p className="text-muted-foreground">{t('notices.heroSubtitle').split('.')[0]}</p>
          </div>
          {checkingDeposit && (
            <div className="flex items-center gap-2 text-emerald-600">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{t('common.loading')}</span>
            </div>
          )}
        </div>

        {/* ParachPay Tag Widget */}
        <motion.div variants={itemVariants}>
          <DashboardTagWidget />
        </motion.div>

        {/* Balance Card */}
        <motion.div variants={itemVariants}>
          <Card className="bg-gradient-to-br from-emerald-950 to-emerald-900 text-white border-0 overflow-hidden relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%2334d399%22 fill-opacity=%220.05%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]" />
            <CardContent className="p-8 relative">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <p className="text-emerald-200 text-sm font-medium mb-1">{t('dashboard.totalBalance')}</p>
                  <p className="text-5xl font-heading font-bold" data-testid="total-balance">
                    {formatCurrency(parseFloat(getTotalBalance()), 'USD')}
                  </p>
                  <p className="text-emerald-300 text-sm mt-2">
                    <Globe className="w-4 h-4 inline mr-1" />
                    {balances.length} {t('exchange.currencies').toLowerCase()}
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  <Link to="/deposit">
                    <Button className="rounded-full bg-white text-emerald-950 hover:bg-emerald-50" data-testid="quick-deposit-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('wallet.deposit')}
                    </Button>
                  </Link>
                  <Link to="/transfer">
                    <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10" data-testid="quick-send-btn">
                      <ArrowUpRight className="w-4 h-4 mr-2" />
                      {t('transfer.send')}
                    </Button>
                  </Link>
                  <Link to="/exchange">
                    <Button variant="outline" className="rounded-full border-white/20 text-white hover:bg-white/10" data-testid="quick-exchange-btn">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      {t('nav.exchange')}
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Spending Limit Alert */}
        <SpendingLimitAlert />

        {/* Currency Wallets */}
        <motion.div variants={itemVariants}>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="font-heading flex items-center gap-2">
                  <Coins className="w-5 h-5 text-emerald-500" />
                  {t('dashboard.myWallets')}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">{t('landing.multiCurrencyDesc')}</p>
              </div>
              <Dialog open={showAddCurrencyDialog} onOpenChange={setShowAddCurrencyDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-full" data-testid="add-currency-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('dashboard.addCurrency')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{t('dashboard.addCurrency')}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <p className="text-sm text-muted-foreground">
                      {t('landing.multiCurrencyDesc')}
                    </p>
                    <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                      <SelectTrigger className="w-full" data-testid="currency-select">
                        <SelectValue placeholder={t('wallet.selectCurrency')} />
                      </SelectTrigger>
                      <SelectContent>
                        <ScrollArea className="h-[300px]">
                          {getAvailableCurrencies().map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              <span className="flex items-center gap-2">
                                <span className="font-mono text-sm bg-muted px-1.5 py-0.5 rounded">{c.symbol}</span>
                                <span>{c.code}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </ScrollArea>
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={handleAddCurrency} 
                      disabled={!selectedCurrency || addingCurrency}
                      className="w-full"
                      data-testid="confirm-add-currency-btn"
                    >
                      {addingCurrency ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          {t('common.create')} {selectedCurrency || ''}
                        </>
                      )}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {balances.map((balance) => (
                  <div 
                    key={balance.currency}
                    className="p-4 rounded-xl border bg-card hover:bg-muted/50 transition-colors"
                    data-testid={`wallet-${balance.currency}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                        <span className="font-mono font-bold text-emerald-600">{balance.symbol}</span>
                      </div>
                      <span className="text-xs px-2 py-1 rounded-full bg-muted font-medium">{balance.currency}</span>
                    </div>
                    <p className="text-2xl font-heading font-bold">
                      {formatCurrency(balance.balance, balance.currency)}
                    </p>
                    <div className="flex gap-2 mt-3">
                      <Link to="/deposit" className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs">
                          <Plus className="w-3 h-3 mr-1" />
                          {t('wallet.addFunds')}
                        </Button>
                      </Link>
                      <Link to="/exchange" className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs">
                          <RefreshCw className="w-3 h-3 mr-1" />
                          {t('nav.exchange')}
                        </Button>
                      </Link>
                    </div>
                  </div>
                ))}
                {balances.length === 0 && (
                  <div className="col-span-full text-center py-8">
                    <Wallet className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">{t('wallet.noCurrencies')}</p>
                    <Button 
                      variant="link" 
                      className="text-emerald-600"
                      onClick={() => setShowAddCurrencyDialog(true)}
                    >
                      {t('dashboard.addCurrency')}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Grid */}
        <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="stat-card" data-testid="stat-total-sent">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('reports.spending')}</p>
                  <p className="text-2xl font-heading font-bold">{formatCurrency(stats?.total_sent || 0, 'USD')}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-red-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-total-received">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('reports.income')}</p>
                  <p className="text-2xl font-heading font-bold">{formatCurrency(stats?.total_received || 0, 'USD')}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-total-deposited">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('wallet.deposit')}</p>
                  <p className="text-2xl font-heading font-bold">{formatCurrency(stats?.total_deposited || 0, 'USD')}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="stat-card" data-testid="stat-total-fees">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('common.fee')}</p>
                  <p className="text-2xl font-heading font-bold">{formatCurrency(stats?.total_fees || 0, 'USD')}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Actions & Quick Exchange */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <motion.div variants={itemVariants}>
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">{t('dashboard.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/deposit" className="block">
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted transition-colors cursor-pointer" data-testid="action-deposit">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                      <Plus className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{t('dashboard.addFunds')}</p>
                      <p className="text-sm text-muted-foreground">{t('landing.multiCurrencyDesc').slice(0, 40)}...</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </Link>
                <Link to="/transfer" className="block">
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted transition-colors cursor-pointer" data-testid="action-send">
                    <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                      <ArrowUpRight className="w-5 h-5 text-blue-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{t('dashboard.sendMoney')}</p>
                      <p className="text-sm text-muted-foreground">{t('notices.sendReceiveDesc').slice(0, 30)}...</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </Link>
                <Link to="/cards" className="block">
                  <div className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted transition-colors cursor-pointer" data-testid="action-cards">
                    <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-orange-500" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{t('cards.virtualCards')}</p>
                      <p className="text-sm text-muted-foreground">{t('landing.virtualCardsDesc').slice(0, 30)}...</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </Link>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Exchange Widget */}
          <motion.div variants={itemVariants}>
            <QuickExchangeWidget onExchangeComplete={fetchData} />
          </motion.div>
        </div>

        {/* Recent Transactions */}
        <motion.div variants={itemVariants}>
          <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading">{t('dashboard.recentTransactions')}</CardTitle>
                <Link to="/transactions">
                  <Button variant="ghost" size="sm" data-testid="view-all-transactions-btn">
                    {t('dashboard.viewAll')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <Wallet className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">{t('dashboard.noTransactions')}</p>
                    <Link to="/deposit">
                      <Button variant="link" className="text-emerald-600">{t('dashboard.addFunds')}</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id}
                        className="flex items-center gap-4 p-4 rounded-xl hover:bg-muted/50 transition-colors transaction-item"
                        data-testid={`transaction-${tx.id}`}
                      >
                        <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                          {getTransactionIcon(tx.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{tx.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(tx.created_at)}
                          </p>
                        </div>
                        <div className="text-right flex items-center gap-2">
                          <div>
                            <p className={`font-mono font-semibold ${tx.type === 'send' ? 'text-red-500' : 'text-emerald-500'}`}>
                              {formatAmount(tx.type, tx.amount, tx.currency)}
                            </p>
                            {tx.fee > 0 && (
                              <p className="text-xs text-muted-foreground">{t('common.fee')}: {formatCurrency(tx.fee, 'USD')}</p>
                            )}
                          </div>
                          <ReceiptButton transactionId={tx.id} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
      </motion.div>
    </Layout>
  );
};

export default Dashboard;
